import React, { Component } from 'react';
import PropTypes from 'prop-types';
import queryString from 'query-string';
import {
  compose,
  filter,
  head,
  propEq,
  prop,
  append,
  map,
  flatten,
  reduce,
  find,
  gte,
  lte,
  and,
  not,
  isEmpty
} from 'ramda';

import api, { handleSearchError } from '../../common/api'
import logger from '../../common/logger';
import { Questionnaire } from './Questionnaire';
import updateState from '../../common/updateState';
import autoBind from 'react-autobind';
import history from '../../history';
import questionTypes from '../questions/questionTypes.json';
import validateAnswers from './validateAnswers';

export class QuestionnaireContainer extends Component {
  constructor(props) {
    super(props);
    autoBind(this);
    const params = queryString.parse(props.location.search);
    let goBack
    if (props.location.state) {
      goBack = props.location.state.goBack
    }

    this.state = {
      busy: false,
      showValidation: false,
      incomplete: true,
      sessionId: params.sessionId,
      questions: [],
      answers: [],
      units: [],
      amountAnswered: 0,
      goBack,
      version: '0'
    };
  }

  componentDidMount() {
    if (this.state.goBack) {
      this.back()
    }

    updateState(this, {
      busy: true
    });
    this.getNextQuestions();
    window.onpopstate = e => history.push({
      pathname: 'questionnaire',
      search: `?sessionId=${this.state.sessionId}`,
      state: { goBack: true }
    })
  }

  getNextQuestions() {
    api().
      get(`questionnaire/${this.state.sessionId}/questions`).
      then(function(response) {
        const { questions, version } = response.data;

        if (questions.length > 0) {
          const answers = flatten(map(({ questionId, options }) =>
            map(({ answerId, value, meta }) => ({
              questionId,
              answerId,
              value,
              meta
            }), filter(option => option.answered, options)), questions))

          updateState(this, {
            busy: false,
            incomplete: this.areAnyQuestionsIncomplete(questions, []),
            questions,
            answers,
            amountAnswered: answers.length,
            version
          })
          return;
        }

        history.push({
          pathname: '/submit',
          search: `?sessionId=${this.state.sessionId}`
        })

      }.bind(this)).
      catch(handleSearchError(`?sessionId=${this.state.sessionId}`))
  }

  back() {
    updateState(this, {
      busy: true
    });
    api().
      delete(`questionnaire/${this.state.sessionId}/answers`).
      then(
        function(response) {
          this.getNextQuestions();
        }.bind(this)
      ).
      catch(handleSearchError(`?sessionId=${this.state.sessionId}`))
  }

  next() {
    const { answers, questions } = this.state;
    updateState(this, {
      busy: true
    });

    const newQuestions = validateAnswers(questions, answers);

    if (newQuestions.valid) {
      api().
        post(`questionnaire/${this.state.sessionId}/answers`,
          {
            answers: answers
          }
        ).
        then(
          function(response) {
            this.getNextQuestions();
          }.bind(this)
        ).
        catch(handleSearchError(`?sessionId=${this.state.sessionId}`))
    } else {
      updateState(this, {
        questions: newQuestions.questions,
        showValidation: true
      });
    }
  }

  onAnswer(questionId, questionType, answerId, value, meta) {
    switch (questionType) {
    case questionTypes.yesNo:
      this.updateMultipleChoiceAnswer(questionId, answerId, value);
      break;
    case questionTypes.multipleChoice:
      this.updateMultipleChoiceAnswer(questionId, answerId);
      break;
    case questionTypes.multipleAnswer:
      this.updateMultipleAnswerAnswer(questionId, answerId, value);
      break;
    case questionTypes.multipleAnswerCombo:
      this.updateMultipleAnswerAnswerCombo(questionId, answerId, value);
      break;
    case questionTypes.number:
    case questionTypes.weight:
    case questionTypes.height:
      this.updateNumberAnswer(questionId, answerId, value, meta);
      break;
    case questionTypes.text:
      this.updateTextAnswer(questionId, answerId, value);
      break;
    case questionTypes.range:
      this.updateRangeAnswer(questionId, value);
      break;
    default:
      return logger.error(
        `Unable to handle onAnswer for invalid question type '${questionType}'`
      );
    }
    const { questions, answers } = this.state;
    this.setState({
      incomplete: this.areAnyQuestionsIncomplete(questions, answers),
      amountAnswered: this.amountAnswered(questions, answers)
    });
  }

  onUnitsChange(questionId, unitType, unit) {
    const { units } = this.state;
    const index = units.map(unit => unit.questionId).indexOf(questionId);
    if (index > -1) {
      units.splice(index, 1);
    }

    units.push({
      questionId: questionId,
      unitType: unitType,
      value: unit
    });

    updateState(this, {
      units: units
    });
  }

  updateMultipleAnswerAnswerCombo(questionId, answerId, value){
    let newAnswers = filter(answer => answer.answerId !== answerId, this.state.answers);

    //if nothing was removed then this is a new answer
    if (newAnswers.length === this.state.answers.length) {
      newAnswers = append({ questionId, answerId }, newAnswers);
    }

    this.setState({
      answers: newAnswers
    });
  }

  updateMultipleAnswerAnswer(questionId, answerId, value) {
    let newAnswers = filter(answer => answer.answerId !== answerId, this.state.answers);

    //if nothing was removed then this is a new answer
    if (newAnswers.length === this.state.answers.length) {
      newAnswers = append({ questionId, answerId }, newAnswers);
    }

    this.setState({
      answers: newAnswers
    });
  }

  updateTextAnswer(questionId, answerId, value) {
    this.updateNumberAnswer(questionId, answerId, value);
  }

  updateNumberAnswer(questionId, answerId, value, meta) {

    const answers = map((answer) => {
      if (answer.questionId !== questionId) {
        return answer;
      }
      return {
        questionId,
        answerId,
        value,
        meta
      };
    }, this.state.answers.length === 0 ? [ { questionId } ] : this.state.answers);

    this.setState({
      answers
    });
  }

  updateRangeAnswer(questionId, value, state) {
    const { questions } = this.state;

    const question = find(propEq('questionId', questionId), questions);
    const numValue = Number.parseFloat(value);
    let answerId = '';

    if (!isEmpty(value)) {
      if (isNaN(numValue)) {
        return;
      }

      answerId = compose(
        prop('answerId'),
        head,
        filter((option) => and(gte(option.rangeEnd, numValue), lte(option.rangeStart, numValue))),
        prop('options')
      )(question);
    }
    const appendAnswer = append({
      questionId,
      answerId,
      value
    });
    const removeOldAnswer = filter(answer => {
      return not(propEq('questionId', questionId, answer));
    });
    const newAnswers = compose(appendAnswer, removeOldAnswer)(this.state.answers);
    this.setState({ answers: newAnswers });
  }

  updateMultipleChoiceAnswer(questionId, answerId, value) {
    const { answers } = this.state;
    const index = answers.map(answer => answer.questionId).indexOf(questionId);
    if (index > -1) {
      answers.splice(index, 1);
    }

    this.setState({
      answers: append({
        questionId: questionId,
        answerId: answerId,
        value
      }, answers)
    });
  }

  areAnyQuestionsIncomplete(questions, answers) {
    const questionIds = questions.
      filter(question => question.type !== questionTypes.multipleAnswer).
      map(question => question.questionId);

    const answeredQuestionIds = answers.map(answer => answer.questionId);
    let incomplete = false;
    questionIds.forEach(questionId => {
      if (answeredQuestionIds.indexOf(questionId) < 0) {
        return (incomplete = true);
      }
    }, this);

    const questionIdsMultiAnswer = questions.
      filter(question => {
        if (question.type === questionTypes.multipleAnswer) {
          return true;
        }
        return false;
      }).
      map(question => question.questionId);

    const multipleAnsweredQuestionIds = answers.map(
      answer => answer.questionId
    );
    let foundAnswer = false;
    questionIdsMultiAnswer.forEach(questionId => {
      if (multipleAnsweredQuestionIds.indexOf(questionId) >= 0) {
        foundAnswer = true;
      }
    });

    if (!foundAnswer && questionIdsMultiAnswer.length > 0) {
      incomplete = true;
    }

    return incomplete;
  }

  amountAnswered(questions, answers) {
    return reduce(
      (answered, question) => {
        return (
          answered +
          (find(answer => answer.questionId === question.questionId, answers)
            ? 1
            : 0)
        );
      },
      0,
      questions
    );
  }

  render() {
    const {
      busy,
      showValidation,
      incomplete,
      questions,
      answers,
      units,
      amountAnswered,
      version
    } = this.state;
    return (
      <Questionnaire
        version={version}
        busy={busy}
        incomplete={incomplete}
        questions={questions}
        answers={answers}
        units={units}
        onAnswer={this.onAnswer}
        onUnitsChange={this.onUnitsChange}
        back={this.back}
        next={this.next}
        showValidation={showValidation}
        amountAnswered={amountAnswered}
      />
    );
  }
}

QuestionnaireContainer.propTypes = {
  location: PropTypes.shape({
    search: PropTypes.string.isRequired
  })
};




// WEBPACK FOOTER //
// ./src/components/questionnaire/QuestionnaireContainer.js